/**
 * components get re-exported (or re-implemented) here because it makes the
 * build size smaller and gives every page a single place to import UI pieces from
 */
import React, { ReactNode, useEffect, useState } from 'react';
import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faCopy, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DECIMALS, DIGITS } from '@multiversx/sdk-dapp-utils/out/constants';
import { FormatAmountController } from '@multiversx/sdk-dapp/out/controllers/FormatAmountController';
import { TransactionsTableController } from '@multiversx/sdk-dapp/out/controllers/TransactionsTableController';
import copy from 'copy-to-clipboard';
import { useGetAccount, useGetNetworkConfig } from 'hooks';
import { ServerTransactionType } from 'types';

interface CopyButtonProps {
  text: string;
  className?: string;
  iconClass?: string;
}

export const CopyButton = ({
  text,
  className = '',
  iconClass
}: CopyButtonProps) => (
  <button
    type='button'
    className={`btn btn-link p-0 ${className}`}
    onClick={() => copy(text)}
  >
    <FontAwesomeIcon icon={faCopy} className={iconClass} />
  </button>
);

interface FormatAmountProps {
  value: string;
  className?: string;
  showLabel?: boolean;
  'data-testid'?: string;
}

export const FormatAmount = ({
  value,
  className = '',
  showLabel = true,
  ...rest
}: FormatAmountProps) => {
  const {
    network: { egldLabel }
  } = useGetNetworkConfig();

  const { isValid, valueDecimal, valueInteger, label } =
    FormatAmountController.getData({
      digits: DIGITS,
      decimals: DECIMALS,
      egldLabel,
      input: value
    });

  if (!isValid) {
    return null;
  }

  return (
    <span className={className} data-testid={rest['data-testid']}>
      {valueInteger}
      {valueDecimal && <span className='opacity-6'>{valueDecimal}</span>}
      {showLabel && label && ` ${label}`}
    </span>
  );
};

interface TransactionsTableProps {
  transactions?: ServerTransactionType[];
}

export const TransactionsTable = ({
  transactions = []
}: TransactionsTableProps) => {
  const { address } = useGetAccount();
  const { network } = useGetNetworkConfig();
  const [rows, setRows] = useState<
    Awaited<ReturnType<typeof TransactionsTableController.processTransactions>>
  >([]);

  useEffect(() => {
    TransactionsTableController.processTransactions({
      address,
      egldLabel: network.egldLabel,
      explorerAddress: network.explorerAddress,
      transactions
    }).then(setRows);
  }, [transactions]);

  return (
    <table className='table table-striped'>
      <thead>
        <tr>
          <th>Hash</th>
          <th>Method</th>
          <th>From</th>
          <th>To</th>
          <th>Value</th>
          <th>Age</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.txHash}>
            <td>
              <a href={row.link} target='_blank' rel='noreferrer'>
                {row.txHash.substring(0, 10)}...
              </a>
            </td>
            <td>{row.method.name}</td>
            <td title={row.sender.address}>
              {row.sender.name || row.sender.address}
            </td>
            <td title={row.receiver.address}>
              {row.receiver.name || row.receiver.address}
            </td>
            <td>
              {row.value.valueInteger}
              {row.value.valueDecimal} {row.value.ticker ?? row.value.egldLabel}
            </td>
            <td title={row.age.tooltip}>{row.age.timeAgo}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const Loader = () => (
  <div className='d-flex flex-fill align-items-center justify-content-center py-5'>
    <FontAwesomeIcon icon={faSpinner} size='2x' spin className='text-primary' />
  </div>
);

interface PageStateProps {
  icon: IconProp;
  iconClass?: string;
  iconBgClass?: string;
  iconSize?: SizeProp;
  className?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export const PageState = ({
  icon,
  iconClass,
  iconBgClass,
  iconSize = '2x',
  className = '',
  title,
  description,
  action
}: PageStateProps) => (
  <div
    className={`d-flex flex-fill align-items-center justify-content-center flex-column text-center py-5 ${className}`}
  >
    <div
      className={`d-flex align-items-center justify-content-center rounded-circle mb-3 ${
        iconBgClass ?? ''
      }`}
      style={{ width: 64, height: 64 }}
    >
      <FontAwesomeIcon icon={icon} size={iconSize} className={iconClass} />
    </div>
    <h4 data-testid='pageStateTitle'>{title}</h4>
    {description && <p className='mb-4'>{description}</p>}
    {action}
  </div>
);
